import axios from 'axios';
import { clientViewSessionId, announceClientViewError } from './clientViewAs';
import { mockEvents, mockOrders, getEventBySlug } from './mockData';
import { getApiBaseUrl } from './apiBase';
import {
  clearMaintenanceNotice,
  publishMaintenanceNotice
} from './maintenance';

// Check if we should use mock data (no backend available)
const USE_MOCK = false; // Set to true to use mock data without backend

const api = axios.create({
  // Keep frontend requests on same origin so /api proxy can route internally
  // even if api.lovevibesmusic.com has temporary DNS issues.
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = 'Bearer ' + token;

  const viewId = clientViewSessionId();
  if (viewId) {
    config.withCredentials = true;
    config.financeViewAs = true;
    config.headers['x-client-view-as-session'] = viewId;
    const method = (config.method || 'get').toLowerCase();
    const control = (method === 'delete' && /^\/admin\/view-as\/sessions\/[^/]+$/.test(config.url)) ||
      (method === 'post' && config.url === '/auth/logout');
    if (!['get', 'head'].includes(method) && !control) {
      const message = 'This is a read-only client view. End the view to make changes.';
      return Promise.reject(Object.assign(new Error(message), { config, response: { status: 403, data: { code: 'CLIENT_VIEW_AS_READ_ONLY', message } } }));
    }
  }

  // Don't set Content-Type for FormData (let browser set it with boundary)
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

api.interceptors.response.use(
  response => {
    if (!publishMaintenanceNotice(response)) {
      clearMaintenanceNotice();
    }
    return response;
  },
  error => {
    if (['CLIENT_VIEW_AS_INACTIVE', 'CLIENT_VIEW_AS_CHANGED', 'CLIENT_VIEW_AS_AUDIT_FAILED'].includes(error.response?.data?.code)) {
      announceClientViewError(error.response.data.message);
    }
    const maintenance = publishMaintenanceNotice(error);
    if (maintenance) {
      error.isScheduledMaintenance = true;
      error.maintenanceMessage = maintenance.message;
    } else if (
      error.response?.status === 401 &&
      error.config?.financeViewAs !== true
    ) {
      localStorage.removeItem('token');
    }
    return Promise.reject(error);
  }
);

// Mock API wrapper that returns mock data when backend is unavailable
const mockApi = {
  get: async (url, config = {}) => {
    if (!USE_MOCK) return api.get(url, config);

    // Simulate network delay
    await new Promise(r => setTimeout(r, 300));

    // Route matching
    if (url.startsWith('/events/') && !url.includes('admin')) {
      const slug = url.split('/events/')[1].split('?')[0];
      const event = getEventBySlug(slug);
      if (event) return { data: { success: true, data: event } };
      throw { response: { status: 404 } };
    }

    if (url.startsWith('/events')) {
      const params = new URLSearchParams(url.split('?')[1] || '');
      let filtered = [...mockEvents];

      const category = params.get('category');
      if (category) filtered = filtered.filter(e => e.categories.includes(category));

      const search = params.get('search');
      if (search) filtered = filtered.filter(e => e.title.toLowerCase().includes(search.toLowerCase()));

      const featured = params.get('featured');
      if (featured === 'true') filtered = filtered.filter(e => e.featured);

      const status = params.get('status');
      if (status && status !== 'published') filtered = filtered.filter(e => e.status === status);
      else filtered = filtered.filter(e => e.status === 'published' || e.status === 'soldout');

      const limit = parseInt(params.get('limit')) || 12;
      filtered = filtered.slice(0, limit);

      return {
        data: {
          success: true,
          data: filtered,
          pagination: { page: 1, pages: 1, total: filtered.length, limit }
        }
      };
    }

    if (url.startsWith('/orders/') && !url.includes('admin')) {
      const id = url.split('/orders/')[1];
      const order = mockOrders.find(o => o._id === id);
      if (order) return { data: { success: true, data: order } };
    }

    if (url === '/orders') {
      return { data: { success: true, data: mockOrders } };
    }

    if (url === '/auth/me') {
      const token = localStorage.getItem('token');
      if (token) {
        return { data: { success: true, data: { id: 'demo', email: 'demo@example.com', firstName: 'Demo', lastName: 'User', role: 'admin' } } };
      }
      throw { response: { status: 401 } };
    }

    if (url.includes('/analytics')) {
      return { data: { success: true, data: { totalRevenue: 12500, totalOrders: 342, activeEvents: 8, totalUsers: 1250, views: 45000 } } };
    }

    if (url.includes('/checkout/session/')) {
      return { data: { success: true, data: { status: 'complete', order: mockOrders[0] } } };
    }

    // Default: try real API
    return api.get(url, config);
  },

  post: async (url, data, config = {}) => {
    if (!USE_MOCK) return api.post(url, data, config);

    await new Promise(r => setTimeout(r, 300));

    if (url === '/auth/login') {
      localStorage.setItem('token', 'mock-token-12345');
      return { data: { success: true, data: { user: { id: 'demo', email: data.email, firstName: 'Demo', lastName: 'User', role: 'admin' }, token: 'mock-token-12345' } } };
    }

    if (url === '/auth/register') {
      localStorage.setItem('token', 'mock-token-12345');
      return { data: { success: true, data: { user: { id: 'demo', email: data.email, firstName: data.firstName, lastName: data.lastName, role: 'user' }, token: 'mock-token-12345' } } };
    }

    if (url === '/checkout/cart') {
      const event = mockEvents.find(e => e._id === data.eventId);
      const items = data.items.map(item => {
        const ticketType = event?.ticketTypes.find(t => t._id === item.ticketTypeId);
        return { ...item, ticketTypeName: ticketType?.name, unitPrice: ticketType?.price };
      });
      return { data: { success: true, data: { _id: 'cart-123', sessionId: 'sess-123', event: data.eventId, items, total: items.reduce((s, i) => s + i.unitPrice * i.quantity, 0), expiresAt: new Date(Date.now() + 8 * 60 * 1000).toISOString() } } };
    }

    if (url === '/checkout/create-session') {
      alert('Demo Mode: In production, this would redirect to Stripe checkout.\n\nSimulating successful purchase...');
      setTimeout(() => { window.location.href = '/checkout/success?session_id=demo-session'; }, 500);
      return { data: { success: true, data: { sessionId: 'demo-session' } } };
    }

    return api.post(url, data, config);
  },

  put: async (url, data, config = {}) => {
    if (!USE_MOCK) return api.put(url, data, config);
    await new Promise(r => setTimeout(r, 300));
    return { data: { success: true, data: { ...data, id: 'demo' } } };
  },

  patch: async (url, data, config = {}) => {
    if (!USE_MOCK) return api.patch(url, data, config);
    await new Promise(r => setTimeout(r, 300));
    return { data: { success: true, data: { ...data, id: 'demo' } } };
  },

  delete: async (url, config = {}) => {
    if (!USE_MOCK) return api.delete(url, config);
    await new Promise(r => setTimeout(r, 300));
    return { data: { success: true } };
  }
};

export default mockApi;
